import React, { useCallback, useMemo, useRef, useState } from "react";
import * as XLSX from "xlsx";
import jsPDF from "jspdf";
import autoTable from "jspdf-autotable";
import MenuBar from "../components/MenuBar";
import HeaderBar from "../components/HeaderBar";
import ForecastFilters from "../features/stockForecast/components/ForecastFilters";
import ForecastTable, {
  forecastRowsForExport,
} from "../features/stockForecast/components/ForecastTable";
import ForecastItemChart from "../features/stockForecast/components/ForecastItemChart";
import {
  parseStockForecastExcel,
  downloadForecastTemplate,
} from "../features/stockForecast/services/excelService";
import { loadSalesHistoryGrouped } from "../features/stockForecast/services/firestoreService";
import { computeForecasts } from "../features/stockForecast/services/forecastService";

const PAGE_SIZE = 50;

export default function StockForecastPage() {
  const fileRef = useRef(null);
  const salesCacheRef = useRef(null);

  const [excelItems, setExcelItems] = useState([]);
  const [forecasts, setForecasts] = useState([]);
  const [branches, setBranches] = useState([]);
  const [search, setSearch] = useState("");
  const [branchFilter, setBranchFilter] = useState("all");
  const [monthWindow, setMonthWindow] = useState("all");
  const [page, setPage] = useState(1);
  const [loading, setLoading] = useState(false);
  const [loadStatus, setLoadStatus] = useState("");
  const [error, setError] = useState(null);
  const [parseWarnings, setParseWarnings] = useState([]);
  const [chartItem, setChartItem] = useState(null);
  const [fileName, setFileName] = useState("");

  const recompute = useCallback(
    (items, salesByItem, branch, monthWin) => {
      const results = computeForecasts(items, salesByItem, {
        branchFilter: branch,
        monthWindow: monthWin,
      });
      setForecasts(results);
      setPage(1);
    },
    []
  );

  const ensureSalesLoaded = useCallback(async () => {
    if (salesCacheRef.current) return salesCacheRef.current;

    setLoadStatus("Loading sales_import_months…");
    const data = await loadSalesHistoryGrouped({
      onProgress: ({ processedWeeks, totalWeeks, totalRows, uniqueItems }) => {
        setLoadStatus(
          `Loading week ${processedWeeks}/${totalWeeks} · ${totalRows.toLocaleString()} sale lines · ${uniqueItems.toLocaleString()} items`
        );
      },
    });
    salesCacheRef.current = data;
    setBranches(data.branches);
    return data;
  }, []);

  const handleFile = async (file) => {
    if (!file) return;
    setError(null);
    setParseWarnings([]);
    setLoading(true);
    setFileName(file.name);

    try {
      setLoadStatus("Parsing Excel…");
      const { items, errors } = await parseStockForecastExcel(file);
      setParseWarnings(errors);
      setExcelItems(items);

      const { byItem } = await ensureSalesLoaded();
      setLoadStatus("Computing forecasts…");
      recompute(items, byItem, branchFilter, monthWindow);
    } catch (e) {
      console.error("Stock forecast failed:", e);
      setError(e?.message || "Failed to process forecast.");
      setForecasts([]);
      setExcelItems([]);
    } finally {
      setLoading(false);
      setLoadStatus("");
    }
  };

  const handleBranchChange = async (val) => {
    setBranchFilter(val);
    if (!excelItems.length || !salesCacheRef.current) return;
    recompute(excelItems, salesCacheRef.current.byItem, val, monthWindow);
  };

  const handleMonthWindowChange = (val) => {
    setMonthWindow(val);
    if (!excelItems.length || !salesCacheRef.current) return;
    recompute(excelItems, salesCacheRef.current.byItem, branchFilter, val);
  };

  const filtered = useMemo(() => {
    const q = search.trim().toLowerCase();
    if (!q) return forecasts;
    return forecasts.filter((r) => r.itemName.toLowerCase().includes(q));
  }, [forecasts, search]);

  const exportExcel = () => {
    if (!filtered.length) return;
    const ws = XLSX.utils.json_to_sheet(forecastRowsForExport(filtered));
    const wb = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(wb, ws, "StockForecast");
    XLSX.writeFile(wb, `stock_forecast_${new Date().toISOString().slice(0, 10)}.xlsx`);
  };

  const exportPdf = () => {
    if (!filtered.length) return;
    const doc = new jsPDF({ orientation: "landscape" });
    doc.setFontSize(14);
    doc.text("Stock Forecast Report", 14, 16);
    doc.setFontSize(9);
    doc.text(`Generated: ${new Date().toLocaleString()}`, 14, 22);

    autoTable(doc, {
      startY: 28,
      head: [
        [
          "Item",
          "Stock",
          "Avg Mo",
          "Avg Day",
          "Fcst 15d",
          "Safety",
          "Recommend",
          "Purchase",
          "Trend",
        ],
      ],
      body: filtered.map((r) => [
        r.itemName,
        r.currentStock ?? "—",
        r.avgMonthlySales.toFixed(1),
        r.avgDailySales.toFixed(2),
        r.forecast15Days.toFixed(1),
        r.safetyStock.toFixed(1),
        r.recommendedQty,
        r.purchaseQty ?? "—",
        r.trend,
      ]),
      styles: { fontSize: 7 },
      headStyles: { fillColor: [51, 65, 85] },
    });
    doc.save(`stock_forecast_${new Date().toISOString().slice(0, 10)}.pdf`);
  };

  return (
    <div className="min-h-screen bg-[#f1f2f4] flex flex-col">
      <MenuBar />
      <HeaderBar />

      <div className="p-4 flex-1 flex flex-col gap-4 max-w-[1600px] w-full mx-auto">
        <div className="flex flex-wrap items-center justify-between gap-3">
          <div>
            <h1 className="text-xl font-bold text-slate-800">Stock Forecast</h1>
            <p className="text-sm text-gray-600">
              Upload items · forecast next 15 days from{" "}
              <code className="text-xs bg-gray-200 px-1 rounded">sales_import_months</code>
            </p>
          </div>
          <div className="flex flex-wrap gap-2">
            <button
              type="button"
              onClick={downloadForecastTemplate}
              className="text-sm px-3 py-1.5 border rounded bg-white hover:bg-gray-50"
            >
              Download template
            </button>
            <button
              type="button"
              onClick={() => fileRef.current?.click()}
              disabled={loading}
              className="text-sm px-4 py-1.5 bg-indigo-600 text-white rounded hover:bg-indigo-700 disabled:opacity-50"
            >
              {loading ? "Processing…" : "Upload Excel"}
            </button>
            <input
              ref={fileRef}
              type="file"
              accept=".xlsx,.xls,.csv"
              className="hidden"
              onChange={(e) => {
                const f = e.target.files?.[0];
                if (f) handleFile(f);
                e.target.value = "";
              }}
            />
          </div>
        </div>

        {fileName && (
          <p className="text-xs text-gray-600">
            File: <strong>{fileName}</strong>
            {excelItems.length > 0 && ` · ${excelItems.length} items`}
          </p>
        )}

        {loadStatus && (
          <div className="text-sm text-indigo-700 bg-indigo-50 border border-indigo-200 rounded px-3 py-2">
            {loadStatus}
          </div>
        )}

        {error && (
          <div className="text-sm text-red-700 bg-red-50 border border-red-200 rounded px-3 py-2">
            {error}
          </div>
        )}

        {parseWarnings.length > 0 && (
          <div className="text-xs text-amber-800 bg-amber-50 border border-amber-200 rounded px-3 py-2 max-h-24 overflow-auto">
            {parseWarnings.slice(0, 10).map((w) => (
              <div key={w}>{w}</div>
            ))}
            {parseWarnings.length > 10 && (
              <div>…and {parseWarnings.length - 10} more warnings</div>
            )}
          </div>
        )}

        <ForecastFilters
          search={search}
          onSearchChange={setSearch}
          branchFilter={branchFilter}
          onBranchChange={handleBranchChange}
          branches={branches}
          monthWindow={monthWindow}
          onMonthWindowChange={handleMonthWindowChange}
          onExportExcel={exportExcel}
          onExportPdf={exportPdf}
          disabled={!filtered.length || loading}
        />

        <ForecastTable
          rows={filtered}
          page={page}
          pageSize={PAGE_SIZE}
          onPageChange={setPage}
          onRowClick={setChartItem}
          loading={loading}
        />

        {!loading && !excelItems.length && (
          <div className="grid sm:grid-cols-3 gap-3 text-sm">
            <div className="bg-white border rounded-lg p-4">
              <h3 className="font-semibold mb-1">1. Upload Excel</h3>
              <p className="text-gray-600 text-xs">
                Columns: ItemName (required), CurrentStock, BranchName (optional).
              </p>
            </div>
            <div className="bg-white border rounded-lg p-4">
              <h3 className="font-semibold mb-1">2. Sales history</h3>
              <p className="text-gray-600 text-xs">
                All uploaded sales weeks are loaded in batches — not queried per item.
              </p>
            </div>
            <div className="bg-white border rounded-lg p-4">
              <h3 className="font-semibold mb-1">3. Forecast</h3>
              <p className="text-gray-600 text-xs">
                15-day forecast with seasonal adjustment, safety stock, and purchase qty.
              </p>
            </div>
          </div>
        )}
      </div>

      {chartItem && (
        <ForecastItemChart result={chartItem} onClose={() => setChartItem(null)} />
      )}
    </div>
  );
}
